import React, { useState, useEffect } from "react";
import axios from "axios";
import { Box, Divider, Flex, Icon, Spinner, Stack, Text } from "@chakra-ui/react";
import { MdError } from "react-icons/md";
import Card from "horizon/components/card/Card";
import ElasticIndexCardHeader from "./ElasticIndexCardHeader";
import ElasticIndexStats from "./ElasticIndexStats";
import ElasticServiceSampleList from "./ElasticServiceSample";

/**
 * Fetches ElasticSearch index data from the debug endpoint.
 * @param {Function} setLoading - Setter for loading state
 * @param {Function} setData - Setter for index data
 * @param {Function} setError - Setter for error message
 * @returns {Promise<void>}
 */
async function fetchIndexData(setLoading, setData, setError) {
    setLoading(true);
    setError(null);
    try {
        const response = await axios.get('/service_search/debug/check-index');
        setData(response.data);
    } catch (err) {
        console.error("Error fetching index data:", err);
        setError("Unable to verify the index");
    } finally {
        setLoading(false);
    }
}

/**
 * Error state display with an icon and message.
 * @param {object} props
 * @param {string} props.error - Error message to display
 * @returns {React.ReactElement}
 */
function IndexCardError({ error }) {
    return (
        <Flex direction="column" align="center" gap="12px" py="32px">
            <Icon as={MdError} w="48px" h="48px" color="red.500" />
            <Text fontSize="sm" color="secondaryGray.600" textAlign="center">{error}</Text>
        </Flex>
    );
}

/**
 * Renders the inner content area: a spinner, an error message, or the index data.
 * @param {object} props
 * @param {object|null} props.data - Fetched index data
 * @param {boolean} props.loading - Whether data is being fetched
 * @param {string|null} props.error - Error message, if any
 * @returns {React.ReactElement}
 */
function IndexCardContent({ data, loading, error }) {
    if (loading) {
        return <Flex justify="center" py="32px"><Spinner color="brand.500" /></Flex>;
    }
    if (error) {
        return <IndexCardError error={error} />;
    }
    const hasSamples = data?.sample_services && data.sample_services.length > 0;
    return (
        <Stack spacing="20px">
            <ElasticIndexStats data={data} />
            {hasSamples && <Divider />}
            {hasSamples && <ElasticServiceSampleList services={data.sample_services} />}
        </Stack>
    );
}

/**
 * Card displaying ElasticSearch index health, document counts, and sample services.
 * Fetches data on mount and on refreshTrigger changes.
 * @param {object} props
 * @param {number} props.refreshTrigger - Counter that triggers a data refresh when changed
 * @param {Function} props.onRefresh - Callback to request a refresh from the parent
 * @returns {React.ReactElement}
 */
function ElasticIndexCard({ refreshTrigger, onRefresh }) {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchIndexData(setLoading, setData, setError);
    }, [refreshTrigger]);

    return (
        <Card h="100%">
            <ElasticIndexCardHeader loading={loading} onRefresh={onRefresh} />
            <Box>
                <IndexCardContent data={data} loading={loading} error={error} />
            </Box>
        </Card>
    );
}

export default ElasticIndexCard;
